import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Badge, Button, DataTable, EmptyState, TextField } from '../../group_leader/components/common'
import { makeT } from '../../group_leader/helpers/i18n'
import { isPresent } from '../../group_leader/helpers/value'
import { FIELDS, displayValue, fieldError, placedCount, submittedValue } from '../helpers/userScored'
import ResultsCard from './ResultsCard'

const t = makeT('group_leader.event_results.user_scored')
// The column labels are the leaderboard's own.
const lbT = makeT('group_leader.event_results.leaderboard')

const SAVING = 'saving'
const SAVED = 'saved'
const FAILED = 'failed'

// A game scored by hand, listing the whole field; row state lives here as DataTable owns the <tr>.
const UserScoredCard = ({ event, onSave, onClear, onDone, reranking = false }) => {
  // { [resultId]: { typed: { field: value }, state, error } }
  const [ rows, setRows ] = useState({})
  const [ openRow, setOpenRow ] = useState(null)
  // Opens read-only like the leaderboard; only entry lists golfers with no position.
  const [ entering, setEntering ] = useState(false)

  const allResults = event.results || []
  // Rows arrive with the scoring job after Finish Setup; until then say so, as the scored games do.
  if (allResults.length === 0) {
    return (
      <ResultsCard title={event.name}>
        <EmptyState title={lbT('computing')}/>
      </ResultsCard>
    )
  }
  const placed = placedCount(allResults)
  const results = entering ? allResults : allResults.filter((result) => isPresent(result.rank))
  const detailsLabel = event.details_label || lbT('col_details')

  const rowOf = (result) => rows[result.result_id] || {}
  const valueOf = (result, field) => {
    const typed = rowOf(result).typed || {}
    return field in typed ? typed[field] : displayValue(field, result[field])
  }
  const patch = (result, changes) => setRows((previous) => ({
    ...previous,
    [result.result_id]: { ...previous[result.result_id], ...changes },
  }))

  // Typed values win over the server's, so every reread drops them.
  const forgetRow = (result) => setRows((previous) => {
    const next = { ...previous }
    delete next[result.result_id]
    return next
  })

  const clearRow = async (result) => {
    patch(result, { state: SAVING, error: null })
    const failed = await onClear(result)
    if (failed) {
      patch(result, { state: FAILED, error: t('errors.clear_failed') })
      return
    }
    forgetRow(result)
  }

  const save = async (result, field) => {
    const raw = valueOf(result, field)
    if (raw === displayValue(field, result[field])) { return }

    const invalid = fieldError(field, raw)
    if (invalid) {
      patch(result, { state: FAILED, error: t(`errors.${invalid}`) })
      return
    }

    patch(result, { state: SAVING, error: null })
    const failed = await onSave(result, field, submittedValue(field, raw))
    patch(result, { state: failed ? FAILED : SAVED, error: failed ? t('errors.save_failed') : null })
  }

  const fieldExtras = { [FIELDS.rank]: { placeholder: t('unplaced') }, [FIELDS.purse]: { prefix: '$' } }
  const field = (result, name, extra = {}) => (
    <TextField
      id={`gl-us-${result.result_id}-${name}`}
      ariaLabel={t(`aria.${name}`, { name: result.player_name })}
      value={valueOf(result, name)}
      onChange={(value) => patch(result, { typed: { ...(rowOf(result).typed || {}), [name]: value } })}
      onBlur={() => save(result, name)}
      className={`gl-us-field gl-us-field--${name}`}
      {...fieldExtras[name]}
      {...extra}
      />
  )

  const rowStatus = (result) => {
    const { state, error } = rowOf(result)
    if (state === SAVING) { return <Badge tone="neutral">{t('saving')}</Badge> }
    if (state === SAVED) { return <Badge tone="success">{t('saved')}</Badge> }
    if (state === FAILED) { return <span className="gl-us-error" role="alert">{error}</span> }
    return null
  }

  const rowActions = (result) => (
    <div className="gl-us-row-actions">
      {rowStatus(result)}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => clearRow(result)}
        disabled={!result.can_clear || rowOf(result).state === SAVING}
        >
        {t('clear')}
      </Button>
    </div>
  )

  const columns = [
    { key: FIELDS.rank, label: lbT('col_pos'), className: 'gl-us-col-pos' },
    { key: 'player', label: lbT('col_player') },
    { key: FIELDS.purse, label: lbT('col_purse'), numeric: true },
    { key: FIELDS.points, label: lbT('col_points'), numeric: true },
    { key: FIELDS.details, label: detailsLabel },
    ...(entering ? [ { key: 'actions', label: <span className="gl-sr-only">{t('columns.actions')}</span> } ] : []),
  ]

  const renderCell = (result, column) => {
    if (column.key === 'player') {
      return <span className="gl-event-results-player-name">{result.player_name}</span>
    }
    if (column.key === 'actions') { return rowActions(result) }
    if (!entering) {
      const shown = displayValue(column.key, result[column.key])
      const prefix = (column.key === FIELDS.purse && shown) ? '$' : ''
      return <span className="gl-event-results-score-val">{`${prefix}${shown}`}</span>
    }
    if (column.key === FIELDS.details) {
      return field(result, FIELDS.details, { ariaLabel: `${detailsLabel}: ${result.player_name}` })
    }
    return field(result, column.key)
  }

  // Four fields do not fit at phone width, so a row opens one at a time.
  const renderMobileRow = (result) => {
    const isOpen = entering && openRow === result.result_id
    const purse = displayValue(FIELDS.purse, result.purse)
    const points = displayValue(FIELDS.points, result.points)
    const summary = [ purse && `$${purse}`, points && t('points_short', { n: points }) ].filter(Boolean).join(' · ')

    return (
      <div className={`gl-us-m-wrap${isOpen ? ' is-open' : ''}`}>
        <div className="gl-event-results-lb-mobile-row">
          <span className="gl-us-m-pos">{valueOf(result, FIELDS.rank) || t('unplaced')}</span>
          <div className="gl-event-results-lb-mobile-name">
            {result.player_name}
            {summary && <span className="gl-us-m-summary">{summary}</span>}
          </div>
          {entering && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOpenRow(isOpen ? null : result.result_id)}
              >
              {isOpen ? t('done') : t('edit')}
            </Button>
          )}
        </div>
        {isOpen && (
          <div className="gl-us-m-fields">
            {field(result, FIELDS.rank)}
            {field(result, FIELDS.purse)}
            {field(result, FIELDS.points)}
            {field(result, FIELDS.details)}
            {rowActions(result)}
          </div>
        )}
      </div>
    )
  }

  return (
    <ResultsCard
      title={event.name}
      subtitle={t('placed', { count: placed, total: allResults.length })}
      >
      {results.length === 0 ? (
        <EmptyState title={t('empty.title')} subtitle={t('empty.body')}/>
      ) : (
        <DataTable
          columns={columns}
          rows={results}
          rowKey={(result) => result.result_id}
          ariaLabel={t('aria.table', { name: event.name })}
          renderCell={renderCell}
          renderMobileRow={renderMobileRow}
          />
      )}
      <div className="gl-us-foot">
        <span className="gl-us-foot-note">{entering ? t('foot_note') : t('scored_by_you')}</span>
        <div className="gl-us-foot-actions">
          <Button
            variant={entering ? 'secondary' : 'primary'}
            size="md"
            loading={entering && reranking}
            onClick={() => {
              if (entering) {
                setRows({})
                onDone()
              }
              setEntering(!entering)
            }}
            >
            {entering ? t('done') : (placed === 0 ? t('enter_results') : t('change_results'))}
          </Button>
        </div>
      </div>
    </ResultsCard>
  )
}

UserScoredCard.propTypes = {
  event: PropTypes.shape({
    event_id: PropTypes.string.isRequired,
    name: PropTypes.string,
    details_label: PropTypes.string,
    results: PropTypes.array,
  }).isRequired,
  // Resolves truthy when the write failed, so the row keeps what was typed and says so
  onSave: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  // Closing the entry view recalculates the positions and rereads the card and the leaderboard
  onDone: PropTypes.func.isRequired,
  reranking: PropTypes.bool,
}

export default UserScoredCard
